import { BreakpointObserver, Breakpoints, LayoutModule } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VerticalMenuMobileItemComponent } from './vertical-menu-mobile-item/vertical-menu-mobile-item.component';
import { NavigationItem, NavigationService } from '../../../public-api';
import { map, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-vertical-menu-mobile-nav',
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    LayoutModule,
    VerticalMenuMobileItemComponent,
  ],
  templateUrl: './vertical-menu-mobile-nav.component.html',
  styleUrl: './vertical-menu-mobile-nav.component.scss'
})
export class VerticalMenuMobileNavComponent {
  @Input() title = 'Smart Parking';
  @Input() logoUrl = 'assets/logo-jjames.webp';
  @Input() autoCollapse = true; // Auto-collapse on mobile

  @Input() navigationItems: NavigationItem[] = [];
  isCollapsed = false;
  isHandset = false;

  private destroy$ = new Subject<void>();
  private breakpointObserver = inject(BreakpointObserver);
  navigationService = inject(NavigationService); // Made public for template access
  router = inject(Router);

  ngOnInit(): void {
    this.setupBreakpoints();
    this.setupNavigation();
  }

  toggleCollapse(): void {
    this.navigationService.setCollapse(!this.isCollapsed);
  }

  navigateTo(route: string): void {
    this.navigationService.navigateTo(route);
    // Auto-collapse on mobile after navigation
    if (this.isHandset) {
      this.navigationService.setCollapse(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupBreakpoints(): void {
    this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.Tablet])
      .pipe(
        takeUntil(this.destroy$),
        map(result => result.matches)
      )
      .subscribe(isHandset => {
        this.isHandset = isHandset;
        // Always collapse on mobile devices
        if (isHandset) {
          this.navigationService.setCollapse(true);
        }
      });
  }

  private setupNavigation(): void {
    // Subscribe to navigation items
    this.navigationService.getNavigationItems().pipe(
      takeUntil(this.destroy$)
    ).subscribe(items => {
      this.navigationItems = items;
    });

    // Subscribe to collapse state
    this.navigationService.getCollapseState().pipe(
      takeUntil(this.destroy$)
    ).subscribe(collapsed => {
      this.isCollapsed = collapsed;
      // Collapse all expanded items when navigation is collapsed
      if (collapsed) {
        this.collapseAllItems();
      }
    });
  }

  private collapseAllItems(): void {
    const collapseRecursive = (items: NavigationItem[]) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          item.expanded = false;
          collapseRecursive(item.children);
        }
      });
    };
    collapseRecursive(this.navigationItems);
  }
}